import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  Card,
  Container,
  Flex,
  Grid,
  Group,
  Text,
  Button,
  Popover,
  Stack,
  Input,
} from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconCheck, IconTrash, IconX, IconArrowsSort, IconSearch } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { HeaderMenu } from '../../components/HeaderMenu/HeaderMenu';
import { VisiLoader, HelpTip } from "../../components/GeneralComponent/GeneralComponent";
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';

type AggregatedReportItem = {
  report_id: string;
  report_name: string;
  campaign_id?: string;
  site_id?: string;
  created_date?: string;
  updated_date?: string;
};

export function AggregatedReports() {
  useDocumentTitle('Visiopt - Aggregated Reports');
  const navigate = useNavigate();

  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [reportList, setReportList] = useState<AggregatedReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Same four options as the campaign report listing; the backend does the ordering.
  const [sortModalOpened, setSortModalOpened] = useState(false);
  const [selectedSort, setSelectedSort] = useState<string | null>(null);
  // Name search filters what is already loaded; sorting is the only thing the backend redoes.
  const [searchKeyword, setSearchKeyword] = useState('');

  const handleHeaderMenuComponentChange = (value: any) => {
    setSelectedMemberWebsite(value);
  };

  const loadReports = useCallback((sortingType?: string | null) => {
    setLoading(true);
    AggregatedReportsAPI.getreportlist(sortingType ? { sorting_type: sortingType } : {})
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1) {
          setReportList(data.data.aggregated_report_list || []);
        } else if (data) {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message || 'Could not load aggregated reports.',
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  useEffect(() => {
    if (selectedMemberWebsite === '') { return; }
    loadReports(selectedSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMemberWebsite, loadReports]);

  const handleSort = (option: string) => {
    setSortModalOpened(false);
    setSelectedSort(option);
    loadReports(option);
  };

  const visibleReports = searchKeyword.trim() === ''
    ? reportList
    : reportList.filter((r) =>
        (r.report_name || '').toLowerCase().includes(searchKeyword.trim().toLowerCase())
      );

  const openReport = (reportId: string) => {
    navigate(`/aggregated-report/${encodeURIComponent(reportId)}`);
  };

  // Draft mode — Report page only INSERTs the row on first Save View.
  const createReport = () => {
    navigate('/aggregated-report/new');
  };

  const performDelete = (report: AggregatedReportItem) => {
    // Optimistic removal; reverted on failure.
    const snapshot = reportList;
    setReportList((prev) => prev.filter((r) => r.report_id !== report.report_id));

    AggregatedReportsAPI.deletereport({ report_id: report.report_id })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1) {
          notifications.show({
            color: 'green',
            icon: <IconCheck size="1.1rem" />,
            title: 'Deleted',
            message: `"${report.report_name}" has been removed.`,
          });
        } else {
          setReportList(snapshot);
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: (data && data.message) || 'Could not delete the report.',
          });
        }
      })
      .catch(() => {
        setReportList(snapshot);
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: 'Could not delete the report.',
        });
      });
  };


  const confirmDelete = (report: AggregatedReportItem) =>
    modals.openConfirmModal({
      trapFocus: false,
      title: 'Please confirm your action',
      children: (
        <Text size="sm">
          Are you sure you want to delete{' '}
          <Text component="span" weight={600}>
            {report.report_name}
          </Text>
          ?
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => performDelete(report),
    });

  return (
    <>
      <VisiLoader VisiLoaderVisible={loading} />
      <HeaderMenu
        HeaderMenuValue={selectedMemberWebsite}
        HeaderMenuOnChange={handleHeaderMenuComponentChange}
      />
      <Container size="xl">
        <Card shadow="sm" padding="sm" radius="md" withBorder>
          <Group position="apart" mb="xs">
            <Text weight={500}>AGGREGATED REPORTS <HelpTip helpId={382} /></Text>
          </Group>
          <Button type="button" onClick={createReport}>
            Create a New Report
          </Button>

          {reportList.length > 0 && (
            <Box
              p={4}
              pl={20}
              mt={10}
              sx={(theme) => ({
                padding: '10px',
                borderRadius: theme.radius.md,
                fontWeight: 'bold',
              })}
            >
            <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            <Popover
              opened={sortModalOpened}
              onChange={setSortModalOpened}
              position="bottom-start"
              shadow="md"
              width={150}
              withinPortal
            >
              <Popover.Target>
                <Text
                  mt={3}
                  weight={500}
                  onClick={() => setSortModalOpened((o) => !o)}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                >
                  <Group spacing={5} align="center">
                    <span style={{ fontSize: 18 }}>Sort</span>
                    <IconArrowsSort stroke={1.5} />
                  </Group>
                </Text>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack spacing={5}>
                  <Button variant={selectedSort === 'az' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('az')}>A – Z</Button>
                  <Button variant={selectedSort === 'za' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('za')}>Z – A</Button>
                  <Button variant={selectedSort === 'oldest' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('oldest')}>Oldest</Button>
                  <Button variant={selectedSort === 'newest' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('newest')}>Newest</Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>
              <Input
                icon={<IconSearch size={16} />}
                placeholder="Search"
                maxLength={50}
                value={searchKeyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKeyword(e.currentTarget.value)}
                style={{ maxWidth: '250px' }}
              />
            </Flex>
            </Box>
          )}

          {!loading && visibleReports.length === 0 ? (
            <Box
              p="xl"
              sx={(theme) => ({
                textAlign: 'center',
                color:
                  theme.colorScheme === 'dark'
                    ? theme.colors.dark[2]
                    : theme.colors.gray[6],
              })}
            >
              <Text size="sm">
                {reportList.length === 0
                  ? 'No aggregated reports yet.'
                  : 'No aggregated reports match your search.'}
              </Text>
            </Box>
          ) : (
            visibleReports.map((report) => (
              <Box
                key={report.report_id}
                p={4}
                pl={20}
                mt={10}
                sx={(theme) => ({
                  backgroundColor:
                    theme.colorScheme === 'dark'
                      ? theme.colors.dark[5]
                      : '#f8f9fa',
                  padding: '10px',
                  borderRadius: theme.radius.md,
                  border: '0.0625rem solid transparent',
                  borderColor:
                    theme.colorScheme === 'dark'
                      ? theme.colors.dark[1]
                      : '#dee2e6',
                })}
              >
                <Grid gutter="xs" align="center">
                  <Grid.Col span={10}>
                    <Text
                      mt={3}
                      onClick={() => openReport(report.report_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {report.report_name}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <Flex justify="flex-end" align="center">
                      <ActionIcon
                        variant="default"
                        size="lg"
                        mx={3}
                        title="Delete"
                        onClick={() => confirmDelete(report)}
                      >
                        <IconTrash size="1.1rem" />
                      </ActionIcon>
                    </Flex>
                  </Grid.Col>
                </Grid>
              </Box>
            ))
          )}
        </Card>
      </Container>
    </>
  );
}
